import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Zap, Droplets, MapPin, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lubumbashi neighborhoods with approximate coordinates
const NEIGHBORHOODS = [
  { name: 'Commune de Lubumbashi', lat: -11.6600, lng: 27.4794 },
  { name: 'Commune de Kenya', lat: -11.6450, lng: 27.4700 },
  { name: 'Commune de Kamalondo', lat: -11.6700, lng: 27.4600 },
  { name: 'Commune de Katuba', lat: -11.6300, lng: 27.4500 },
  { name: 'Commune de Kampemba', lat: -11.6100, lng: 27.4900 },
  { name: 'Commune de Ruashi', lat: -11.6200, lng: 27.5200 },
  { name: 'Commune Annexe', lat: -11.6800, lng: 27.5000 },
  { name: 'Plateau Karavia', lat: -11.6550, lng: 27.4650 },
  { name: 'Golf Lido', lat: -11.6400, lng: 27.4850 },
  { name: 'Quartier Industriel', lat: -11.6500, lng: 27.4400 },
];

export const ReportOutageModal = ({ open, onOpenChange, onOutageCreated }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    type: 'electricity',
    description: '',
    location_name: '',
    severity: 'medium',
    latitude: -11.6600,
    longitude: 27.4794
  });

  const handleLocationChange = (locationName) => {
    const location = NEIGHBORHOODS.find(n => n.name === locationName);
    if (location) {
      setForm({
        ...form,
        location_name: locationName,
        latitude: location.lat,
        longitude: location.lng
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/outages`, form);
      toast.success(t('report_success'));
      onOutageCreated?.(response.data);
      onOpenChange(false);
      setForm({
        type: 'electricity',
        description: '',
        location_name: '',
        severity: 'medium',
        latitude: -11.6600,
        longitude: 27.4794
      });
    } catch (error) {
      const message = error.response?.data?.detail || t('report_error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border" data-testid="report-outage-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-primary" />
            {t('report_title')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('report_description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {t('report_type')}
            </Label>
            <RadioGroup
              value={form.type}
              onValueChange={(value) => setForm({ ...form, type: value })}
              className="grid grid-cols-2 gap-4"
            >
              <Label
                htmlFor="type-electricity"
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  form.type === 'electricity'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="electricity" id="type-electricity" className="sr-only" />
                <div className={`p-2 rounded-lg ${form.type === 'electricity' ? 'bg-primary' : 'bg-muted'}`}>
                  <Zap className={`h-5 w-5 ${form.type === 'electricity' ? 'text-black' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <p className="font-semibold">{t('report_electricity')}</p>
                  <p className="text-xs text-muted-foreground">SNEL</p>
                </div>
              </Label>
              
              <Label
                htmlFor="type-water"
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  form.type === 'water'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-border hover:border-blue-500/50'
                }`}
              >
                <RadioGroupItem value="water" id="type-water" className="sr-only" />
                <div className={`p-2 rounded-lg ${form.type === 'water' ? 'bg-blue-500' : 'bg-muted'}`}>
                  <Droplets className={`h-5 w-5 ${form.type === 'water' ? 'text-white' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <p className="font-semibold">{t('report_water')}</p>
                  <p className="text-xs text-muted-foreground">REGIDESO</p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label className="text-sm font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {t('report_location')}
            </Label>
            <Select value={form.location_name} onValueChange={handleLocationChange}>
              <SelectTrigger className="h-12 bg-input/50 border-transparent" data-testid="location-select">
                <SelectValue placeholder={t('report_location_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {NEIGHBORHOODS.map((neighborhood) => (
                  <SelectItem key={neighborhood.name} value={neighborhood.name}>
                    {neighborhood.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {t('report_severity')}
            </Label>
            <Select value={form.severity} onValueChange={(value) => setForm({ ...form, severity: value })}>
              <SelectTrigger className="h-12 bg-input/50 border-transparent" data-testid="severity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    {t('report_severity_low')}
                  </span>
                </SelectItem>
                <SelectItem value="medium">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    {t('report_severity_medium')}
                  </span>
                </SelectItem>
                <SelectItem value="high">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    {t('report_severity_high')}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {t('report_description_label')}
            </Label>
            <Textarea
              id="description"
              placeholder={t('report_description_placeholder')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="min-h-[100px] bg-input/50 border-transparent focus:border-primary resize-none"
              required
              data-testid="description-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-primary/90 btn-glow"
            disabled={loading || !form.location_name || !form.description}
            data-testid="submit-outage-btn"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              t('report_submit')
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
